import { storeToRefs } from 'pinia'
import { useTrackStore } from '@ve-pro/store/tracks'
import { useProjectStore } from '@ve-pro/store/project'
import { usePlayerStore } from '@ve-pro/store/player'
import { saveProject } from '@ve-pro/api/project'
import { isSegmentGroup } from '@ve-pro/store/tracks/segment/segment-group'
import type {
  SegmentCollection,
  SegmentFrame,
  Track,
} from '@ve-pro/types/track.type'
import { ProjectSegmentModel } from '@ve-pro/api/project/type'
import { isSupportFade } from '@ve-pro/store/tracks/segment/segment-fade'
import {
  isSupportAnimation,
  isSupportAnimationInout,
  isSupportAnimationLoop,
} from '@ve-pro/store/tracks/segment/segment-animation'
import { isInteger } from 'lodash-es'
import { isSegmentFragment } from '@ve-pro/store/tracks/segment/segment-fragment'
import { durationToFrames } from '@ve-pro/store/tracks/frame'
import { hasSegmentMaterial } from '@ve-pro/store/tracks/resolve-segment'
import { retryPromise } from '@ve-pro/ve/utils/promise'
import { resolveConfigCopy } from '@ve-pro/store/project/config'
import PromiseQueue from 'promise-queue'

const maxConcurrent = 1
const maxQueue = Infinity
const saveQueue = new PromiseQueue(maxConcurrent, maxQueue)

export function useSave(
  parasm: {
    force?: boolean
  } = {},
): ReturnType<typeof saveProject> {
  const trackStore = useTrackStore()
  const projectStore = useProjectStore()
  const playerStore = usePlayerStore()
  const { id, projectName, ratio } = storeToRefs(projectStore)
  const { tracks } = storeToRefs(trackStore)

  // 空间满了，就不能进行保存了
  if (projectStore.isFullStorage) {
    return Promise.reject()
  }

  // 如果是模版，并且是审核中或通过的，就不能进行保存了
  if (projectStore.isCommonTemplateProject && projectStore.isAudited) {
    return Promise.reject()
  }

  projectStore.updateTime = new Date().getTime()

  // 强制保存，不需要再次确认
  parasm.force = true
  const fn = () => {
    const _tracks: typeof tracks.value = tracks.value.map((t) => {
      const segments: SegmentCollection[] = []
      t.segments.forEach((segment) => {
        segments.push(segment)
        if (isSegmentGroup(segment)) {
          const children = trackStore.segmentGroups[segment.id] ?? []
          children.forEach((child) => (child.track_id = segment.track_id))
          segments.push(...children)
        }
      })
      return { ...t, segments }
    })

    return saveProject({
      last_stage: projectStore.mode,
      track_mode: trackStore.mode,
      duration: playerStore.totalTime,
      id: id.value,
      name: projectName.value,
      ratio: ratio.value,
      ratios: projectStore.ratios.join(','),
      languages: projectStore.languages.join(','),
      tracks: resolveSavingTracks(_tracks),
      orientation_adapter: projectStore.orientationAdapter,
      config: JSON.stringify(resolveConfigCopy(projectStore.configs)),
      ...parasm,
    })
  }

  return saveQueue.add(() =>
    retryPromise(fn, {
      max: 10,
      delay: 1000,
      name: `saved project`,
    }),
  )
}
export function resolveSavingTracks(tracks: Track[]): Track[] {
  return tracks.map<Track>((t) => {
    return {
      ...t,
      segments:
        t.segments?.map((s) => {
          const copied: SegmentCollection &
            Pick<
              ProjectSegmentModel,
              'duration_frames_rate' | 'trim_start_frame_rate'
            > = {
            ...s,
            asset_type: hasSegmentMaterial(s)
              ? s.material.type
              : s.asset_type ?? (s as any).material?.type,
            asset_id: (hasSegmentMaterial(s)
              ? s.material?.asset_id ?? s.asset_id
              : 0) as never,
            // 在时长数据计算的时候，会出现浮点数，提交的时候把浮点数裁剪掉
            // 只记录一倍速的值
            start_time: Math.floor(s.start_time),
            duration: isSegmentFragment(s)
              ? s.duration
              : Math.floor(s.source_rate.duration),
            trim_start_time: Math.floor(s.source_rate.trim_start_time),

            // 变速后的帧数
            duration_frames_rate: s.duration_frames,
            trim_start_frame_rate: s.trim_start_frame,
          }

          copied.duration_frames = durationToFrames(copied.duration, false)
          copied.trim_start_frame = durationToFrames(
            copied.trim_start_time,
            false,
          )

          // 新增的帧属性，仅在有效帧时候才需要记录
          // 非有效帧（指不满帧的）遵循旧逻辑执行
          const frameProperties: Array<
            | keyof SegmentFrame
            | keyof Pick<
                ProjectSegmentModel,
                'duration_frames_rate' | 'trim_start_frame_rate'
              >
          > = [
            'duration_frames',
            'start_frame',
            'trim_start_frame',
            'duration_frames_rate',
            'trim_start_frame_rate',
          ]
          for (const key of frameProperties) {
            if (!isInteger(copied[key])) {
              delete copied[key]
            }
          }

          if (isSupportFade(copied)) {
            copied.fade_in_duration = Math.floor(copied.fade_in_duration)
            copied.fade_out_duration = Math.floor(copied.fade_out_duration)
          }

          if (isSupportAnimation(copied)) {
            copied.anim_in &&
              (copied.anim_in.duration = Math.floor(copied.anim_in.duration))
            copied.anim_out &&
              (copied.anim_out.duration = Math.floor(copied.anim_out.duration))
          }

          if (isSupportAnimationLoop(copied)) {
            copied.anim_loop &&
              (copied.anim_loop.duration = Math.floor(
                copied.anim_loop.duration,
              ))
          }

          if (isSupportAnimationInout(copied)) {
            copied.anim_inout &&
              (copied.anim_inout.duration = Math.floor(
                copied.anim_inout.duration,
              ))
          }

          return copied
        }) ?? [],
    }
  })
}
